import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ChevronUp, ChevronDown, Save, Folder, Layers, FileText, RotateCcw, Info } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { useSite } from '../../contexts/SiteContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const cloneTree = (t) => JSON.parse(JSON.stringify(t));

function buildTree(pages) {
  const top = [];
  const groupIdx = {};
  const sectionIdx = {};
  [...pages]
    .sort((a, b) => (a.menu_order || 0) - (b.menu_order || 0))
    .forEach((page) => {
      const group = (page.menu_group || '').trim();
      const section = (page.menu_section || '').trim();
      const leaf = { type: 'page', id: page.id, label: page.title, hidden: !page.show_in_menu };
      if (!group) {
        top.push(leaf);
        return;
      }
      if (groupIdx[group] === undefined) {
        groupIdx[group] = top.length;
        top.push({ type: 'group', label: group, children: [] });
      }
      const g = top[groupIdx[group]];
      if (!section) {
        g.children.push(leaf);
        return;
      }
      const key = `${group}||${section}`;
      if (sectionIdx[key] === undefined) {
        sectionIdx[key] = g.children.length;
        g.children.push({ type: 'section', label: section, children: [] });
      }
      g.children[sectionIdx[key]].children.push(leaf);
    });
  return top;
}

function flatten(nodes) {
  const ids = [];
  nodes.forEach((node) => {
    if (node.type === 'page') {
      ids.push(node.id);
    } else if (node.type === 'group') {
      node.children.forEach((child) => {
        if (child.type === 'page') ids.push(child.id);
        else child.children.forEach((leaf) => ids.push(leaf.id));
      });
    }
  });
  return ids;
}

function ArrowButtons({ onUp, onDown, disableUp, disableDown, testPrefix }) {
  return (
    <div className="flex flex-col shrink-0">
      <button
        onClick={onUp}
        disabled={disableUp}
        data-testid={`${testPrefix}-up`}
        aria-label="Move up"
        className="p-0.5 rounded text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
      >
        <ChevronUp className="h-4 w-4" />
      </button>
      <button
        onClick={onDown}
        disabled={disableDown}
        data-testid={`${testPrefix}-down`}
        aria-label="Move down"
        className="p-0.5 rounded text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
      >
        <ChevronDown className="h-4 w-4" />
      </button>
    </div>
  );
}

const slug = (s) => (s || '').toLowerCase().replace(/\s+/g, '-');

export function MenuManager() {
  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const { refreshPages } = useSite();

  const load = async () => {
    try {
      const res = await axios.get(`${API}/pages`, { withCredentials: true });
      setTree(buildTree(res.data));
      setDirty(false);
    } catch (e) {
      toast.error('Failed to load pages');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const moveTop = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= tree.length) return;
    const t = cloneTree(tree);
    [t[i], t[j]] = [t[j], t[i]];
    setTree(t);
    setDirty(true);
  };

  const moveChild = (gi, j, dir) => {
    const t = cloneTree(tree);
    const arr = t[gi].children;
    const k = j + dir;
    if (k < 0 || k >= arr.length) return;
    [arr[j], arr[k]] = [arr[k], arr[j]];
    setTree(t);
    setDirty(true);
  };

  const moveLeaf = (gi, ci, k, dir) => {
    const t = cloneTree(tree);
    const arr = t[gi].children[ci].children;
    const m = k + dir;
    if (m < 0 || m >= arr.length) return;
    [arr[k], arr[m]] = [arr[m], arr[k]];
    setTree(t);
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/pages/reorder`, { ordered_ids: flatten(tree) }, { withCredentials: true });
      refreshPages();
      setDirty(false);
      toast.success('Menu order saved');
    } catch (e) {
      toast.error('Failed to save order');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-1/4" />
          <div className="h-64 bg-muted rounded-lg" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-3xl" data-testid="admin-menu-manager">
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Menu Order</h1>
          <p className="text-muted-foreground">Reorder groups, sub-sections, and pages with the arrows.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={load} disabled={!dirty || saving} data-testid="reset-menu-order-btn">
            <RotateCcw className="mr-2 h-4 w-4" />
            Reset
          </Button>
          <Button onClick={save} disabled={!dirty || saving} data-testid="save-menu-order-btn">
            <Save className="mr-2 h-4 w-4" />
            {saving ? 'Saving…' : 'Save Order'}
          </Button>
        </div>
      </div>

      <div className="flex items-start gap-2 rounded-lg border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground mb-6">
        <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
        <span>“Home” and “Contact” are fixed (always first and last). Only your pages, groups, and sub-sections are ordered here.</span>
      </div>

      {tree.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-border rounded-lg">
          <p className="text-muted-foreground">No menu pages yet. Create pages and assign a Menu Group / Sub-section.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {tree.map((node, i) => {
            if (node.type === 'page') {
              return (
                <div
                  key={node.id}
                  data-testid={`menu-item-${slug(node.label)}`}
                  className="flex items-center gap-3 rounded-lg border border-border bg-card px-3 py-2.5"
                >
                  <ArrowButtons
                    onUp={() => moveTop(i, -1)}
                    onDown={() => moveTop(i, 1)}
                    disableUp={i === 0}
                    disableDown={i === tree.length - 1}
                    testPrefix={`top-${slug(node.label)}`}
                  />
                  <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                  <span className="text-sm font-medium">{node.label}</span>
                  {node.hidden && <span className="text-[10px] uppercase tracking-wide text-muted-foreground">hidden</span>}
                </div>
              );
            }
            // group
            return (
              <div key={`group-${node.label}`} className="rounded-lg border border-border bg-card" data-testid={`menu-group-${slug(node.label)}`}>
                <div className="flex items-center gap-3 px-3 py-2.5 border-b border-border bg-muted/30">
                  <ArrowButtons
                    onUp={() => moveTop(i, -1)}
                    onDown={() => moveTop(i, 1)}
                    disableUp={i === 0}
                    disableDown={i === tree.length - 1}
                    testPrefix={`top-${slug(node.label)}`}
                  />
                  <Folder className="h-4 w-4 text-primary shrink-0" />
                  <span className="text-sm font-semibold">{node.label}</span>
                </div>
                <div className="p-2 space-y-1.5">
                  {node.children.map((child, j) => {
                    if (child.type === 'page') {
                      return (
                        <div
                          key={child.id}
                          data-testid={`menu-item-${slug(child.label)}`}
                          className="flex items-center gap-3 rounded-md border border-border/60 bg-background px-3 py-2 ml-4"
                        >
                          <ArrowButtons
                            onUp={() => moveChild(i, j, -1)}
                            onDown={() => moveChild(i, j, 1)}
                            disableUp={j === 0}
                            disableDown={j === node.children.length - 1}
                            testPrefix={`child-${slug(child.label)}`}
                          />
                          <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                          <span className="text-sm">{child.label}</span>
                          {child.hidden && <span className="text-[10px] uppercase tracking-wide text-muted-foreground">hidden</span>}
                        </div>
                      );
                    }
                    // section
                    return (
                      <div key={`section-${child.label}`} className="rounded-md border border-border/60 bg-background ml-4" data-testid={`menu-section-${slug(child.label)}`}>
                        <div className="flex items-center gap-3 px-3 py-2 border-b border-border/60">
                          <ArrowButtons
                            onUp={() => moveChild(i, j, -1)}
                            onDown={() => moveChild(i, j, 1)}
                            disableUp={j === 0}
                            disableDown={j === node.children.length - 1}
                            testPrefix={`section-${slug(child.label)}`}
                          />
                          <Layers className="h-4 w-4 text-primary shrink-0" />
                          <span className="text-sm font-medium">{child.label}</span>
                        </div>
                        <div className="p-2 space-y-1.5">
                          {child.children.map((leaf, k) => (
                            <div
                              key={leaf.id}
                              data-testid={`menu-item-${slug(leaf.label)}`}
                              className="flex items-center gap-3 rounded-md border border-border/50 bg-card px-3 py-2 ml-4"
                            >
                              <ArrowButtons
                                onUp={() => moveLeaf(i, j, k, -1)}
                                onDown={() => moveLeaf(i, j, k, 1)}
                                disableUp={k === 0}
                                disableDown={k === child.children.length - 1}
                                testPrefix={`leaf-${slug(leaf.label)}`}
                              />
                              <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                              <span className="text-sm">{leaf.label}</span>
                              {leaf.hidden && <span className="text-[10px] uppercase tracking-wide text-muted-foreground">hidden</span>}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
